import { isGatewayCardOrProgram } from '@Utils/gatewayCards';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';

import { CancelCardModal } from '@ReactComponents/CancelCardModal';
import { ConfirmModal } from '@ReactComponents/ConfirmModal';
import { CardsGatewayIssuingModalConnected } from '@ReactComponents/Cards/CardsGatewayIssuingModal/CardsGatewayIssuingModal';
import { CardTrackingDetailsModal } from '@ReactComponents/Cards/Components/CardTrackingDetailsModal/CardTrackingDetailsModal';
import { parseAndGetErrorMessage } from '@ReactComponents/Shared/ErrorAlert';

import { AppThunkDispatch, AppThunkGetState } from '@Reducers/index';

import { CardIssuingApiService, IGetIssuingDetailsResponse } from '@ClientApi/cardIssuing';
import { LinkedAccountsApiService } from '@ClientApi/linkedAccounts';
import { TransactionsApi } from '@ClientApi/transactionsApi';
import { CARD_TX_STATE, CARD_TX_STATE_VALUES } from '@Shared/constants';
import { ISearchTransactionsParams } from '@Shared/interfaces/Filters';
import { ICardTransactionModel, IExpenseModel } from '@Shared/models';

import { AddCapabilities } from './cardsActions';
import { CloseModal, ShowModal } from './modalActions';

export const CARD_PAGE_SET_CARD_DETAILS = 'CARD_PAGE_SET_CARD_DETAILS';
export const CARD_PAGE_SET_CARD_STATE = 'CARD_PAGE_SET_CARD_STATE';
export const CARD_PAGE_SET_LOADING = 'CARD_PAGE_SET_LOADING';
export const CARD_PAGE_SET_TRANSACTIONS = 'CARD_PAGE_SET_TRANSACTIONS';

export class SetLoading {
  readonly type = CARD_PAGE_SET_LOADING;
  payload: boolean;
  constructor(loading: boolean) {
    this.payload = loading;
  }
}

export class SetCardDetails {
  readonly type = CARD_PAGE_SET_CARD_DETAILS;
  payload: IGetIssuingDetailsResponse;
  constructor(public details: IGetIssuingDetailsResponse) {
    this.payload = details;
  }
}

export class SetCardTransactions {
  readonly type = CARD_PAGE_SET_TRANSACTIONS;
  payload: { rows: (ICardTransactionModel | IExpenseModel)[]; count: number };
  constructor(public rows: (ICardTransactionModel | IExpenseModel)[], public count: number) {
    this.payload = { rows, count };
  }
}

export class SetCardState {
  readonly type = CARD_PAGE_SET_CARD_STATE;
  payload: string;
  constructor(public state: string) {
    this.payload = state;
  }
}

export const init = (id: string) => async (dispatch: AppThunkDispatch, getState: AppThunkGetState) => {
  const capabilitiesMap = getState().cards.capabilities;

  dispatch(new SetLoading(true));

  const details = await CardIssuingApiService.getIssuingDetails(id, true, true);
  dispatch(new SetCardDetails(details));

  if (isGatewayCardOrProgram(details) && !capabilitiesMap[details.parent_linked_account_id]) {
    const capabilities = await LinkedAccountsApiService.getAccountCapabilities(details.parent_linked_account_id);
    dispatch(new AddCapabilities({ [details.parent_linked_account_id]: capabilities }));
  }

  dispatch(new SetLoading(false));
};

export const getTransactions =
  (dispatch: Dispatch<AnyAction>) =>
  async (
    id: string,
    page: number,
    pageSize: number,
    state?: string | null,
    sortField?: string,
    sortDirection?: 'asc' | 'desc',
    forPersonalCard?: boolean
  ) => {
    // get transactions based on feature flag
    let transactions;
    // don't show duplicates
    const allStates: string[] = [...CARD_TX_STATE_VALUES].filter((cardTxState) => cardTxState !== CARD_TX_STATE.DUPLICATE);
    // get combined transactions to support new expense modal
    let stateFilter;
    if (!state || state === 'all') {
      stateFilter = allStates;
    } else if (state !== 'default') {
      stateFilter = [state];
    }

    const searchParams: ISearchTransactionsParams = {
      limit: pageSize,
      offset: page * pageSize,
      filters: {
        // filter by only the selected card id
        linked_account_ids: [id],
        ...(stateFilter ? { status: stateFilter } : {})
      },
      sort: sortField || 'posted_date',
      direction: sortDirection || 'desc'
    };
    transactions = await TransactionsApi.getCombinedTransactions(searchParams, 'search', null, forPersonalCard);
    dispatch(new SetCardTransactions(transactions.rows, transactions.count));
  };

export const suspend = (dispatch: Dispatch<AnyAction>) => async (id: string) => {
  dispatch(new SetLoading(true));
  const updatedCard = await LinkedAccountsApiService.suspendCard(id);
  dispatch(new SetCardState(updatedCard.state));
  toast.success('This card has been suspended!');
  dispatch(new SetLoading(false));
};

export const unsuspend = (dispatch: Dispatch<AnyAction>) => async (id: string) => {
  dispatch(new SetLoading(true));
  const updatedCard = await LinkedAccountsApiService.unsuspendCard(id);
  dispatch(new SetCardState(updatedCard.state));
  toast.success('This card has been unsuspended!');
  dispatch(new SetLoading(false));
};

export const cancel = (dispatch: Dispatch<AnyAction>) => (id: string, name: string) =>
  dispatch(new ShowModal(CancelCardModal, { cardName: name, cardId: id }));

export const edit = (id: string, card: IGetIssuingDetailsResponse) => async (dispatch: AppThunkDispatch, getState: AppThunkGetState) => {
  const capabilitiesMap = getState().cards.capabilities;
  const capabilities = capabilitiesMap[card.parent_linked_account_id];

  if (isGatewayCardOrProgram(card)) {
    dispatch(
      new ShowModal(CardsGatewayIssuingModalConnected, {
        parentLinkedAccountId: card.parent_linked_account_id,
        linkedAccountId: id,
        companyId: window.USER_PROFILE.company_id,
        capabilities,
        onSubmit: (/* updatedCard: ILinkedAccountModel */) => dispatch(init(id))
      })
    );
  }
};

export const remove = (id: string) => async (dispatch: AppThunkDispatch) => {
  dispatch(
    new ShowModal(ConfirmModal, {
      cancelText: 'No, Cancel',
      confirmText: 'Yes, Remove Card',
      onConfirm: async () => {
        try {
          await LinkedAccountsApiService.deleteCard(id);
          toast.success('Card unassigned and deleted');
          window.location.href = '/cards';
        } catch (error) {
          const errorMessage = parseAndGetErrorMessage(error.message);
          toast.error(errorMessage);
          dispatch(new CloseModal());
        }
      },
      children: null
    })
  );
};

export const viewTrackingDetails = (card: any) => async (dispatch: AppThunkDispatch, getState: AppThunkGetState) => {
  dispatch(new ShowModal(CardTrackingDetailsModal, { card, shippingInfo: card.shipping }));
};

export type CardPageAction = SetLoading | SetCardDetails | SetCardState | SetCardTransactions;
